import { RootState } from '../../../index'
import {
	SCHEDULE_TASKS_FILTER_VALUES,
	ScheduleTasks,
} from '../../../../types/schedule'
import { createSelector } from 'reselect'
import dayjs from 'dayjs'
import {
	Task,
	TasksTableCells,
	TASK_STATUS,
	TaskStatusUnion,
	TASK_STATUS_FOR_EXTERNAL_CLINIC,
} from '../../../../types/task'
import { getAnyTitle, getTasksByDate } from '../../../../helpers'
import { selectOrganizationList } from '../../user/selectors'
import { selectPatientsDataArray } from '../../patients/selectors'
import { PatientData } from '../../../../types/patients'
import {
	SystemConfigValueParam,
	SystemTaskStatusesV2,
} from '../../../../types/systemConfig'
import { selectV1V2TaskTypes } from '../../systemConfig/selectors'

export const checkCompletedTask = (status: TaskStatusUnion): boolean => {
	if (!status) return false
	const statusLowerCase = status.toLowerCase()

	return !!(
		statusLowerCase.match(TASK_STATUS.CLOSED.toLowerCase()) ||
		statusLowerCase.match(
			TASK_STATUS_FOR_EXTERNAL_CLINIC.DONE.toLowerCase()
		) ||
		statusLowerCase.match(/\bcomplete\b/g)
	)
}

// Tasks filter callbacks

const filterOverdueTasksCallback = (task: Task): boolean => {
	const currentTimeUTC = dayjs().valueOf()
	const dueDateUTC = dayjs(task?.settings?.dueDate).valueOf()

	return (
		dueDateUTC < currentTimeUTC &&
		dueDateUTC !== dayjs().startOf('day').valueOf() &&
		!checkCompletedTask(task?.settings?.status || '')
	)
}

const filterUpcomingTodayTasksCallback = (task: Task): boolean => {
	const currentTimeUTC = dayjs().valueOf()
	const dueDateUTC = dayjs(task?.settings?.dueDate).valueOf()

	return (
		(dueDateUTC >= currentTimeUTC ||
			dueDateUTC === dayjs().startOf('day').valueOf()) &&
		!checkCompletedTask(task?.settings?.status || '')
	)
}

const filterDoneTodayTasksCallback = (task: Task): boolean => {
	return checkCompletedTask(task?.settings?.status || '')
}

// Tasks selectors

export const selectTasksFilterValue = (state: RootState): string =>
	state.appReducer.schedule?.tasks?.filter || ''

export const selectTasks = (state: RootState): ScheduleTasks =>
	state.appReducer.schedule?.tasks?.tasks || []

export const selectTaskSearch = (state: RootState): string =>
	state.appReducer.schedule?.tasks?.taskSearch || ''

export const selectNumberOfOverdueTasks = createSelector(
	[selectTasks],
	tasks => {
		return tasks.filter(filterOverdueTasksCallback).length
	}
)

export const selectNumberOfDoneTodayTasks = createSelector(
	[selectTasks],
	tasks => {
		return tasks.filter(filterDoneTodayTasksCallback).length
	}
)

export const selectNumberOfUpcomingTodayTasks = createSelector(
	[selectTasks],
	tasks => {
		return tasks.filter(filterUpcomingTodayTasksCallback).length
	}
)

export const selectFilteredTasks = createSelector(
	[selectTasksFilterValue, selectTasks],
	(filterValue, tasks) => {
		if (filterValue === SCHEDULE_TASKS_FILTER_VALUES.UPCOMING_TODAY) {
			return tasks.filter(filterUpcomingTodayTasksCallback)
		}

		if (filterValue === SCHEDULE_TASKS_FILTER_VALUES.DONE_TODAY) {
			return tasks.filter(filterDoneTodayTasksCallback)
		}

		return []
	}
)

export const selectOverdueTasks = createSelector([selectTasks], tasks => {
	const overdueTasks = tasks
		.filter(filterOverdueTasksCallback)
		.sort(
			(a, b) =>
				dayjs(b?.settings?.dueDate).valueOf() -
				dayjs(a?.settings?.dueDate).valueOf()
		)

	return getTasksByDate(overdueTasks)
})

export const selectSelectedTasksCount = (state: RootState): number =>
	state.appReducer?.schedule?.tasks?.selectedTasks?.length || 0

export const selectedTasks = (state: RootState): Array<string> =>
	state.appReducer?.schedule?.tasks?.selectedTasks || []

export const selectedCheckedTaskTypes = (state: RootState): Array<string> =>
	state.appReducer?.schedule?.tasks?.taskTypes || []

export const selectedCeckedTaskPriorities = (state: RootState): Array<string> =>
	state.appReducer?.schedule?.tasks?.taskPriorities || []

export const selectedCeckedTaskStatuses = (state: RootState): Array<string> =>
	state.appReducer?.schedule?.tasks?.taskStatuses || []

export const selectedCeckedTaskAccountes = (state: RootState): Array<string> =>
	state.appReducer?.schedule?.tasks?.taskAccounts || []

export const selectTaskTypesAll = createSelector(
	selectV1V2TaskTypes,
	taskTypes => taskTypes.map(type => type.value) || []
)

export const selectTaskPrioritiesAll = (state: RootState): Array<string> =>
	state.systemConfig?.system?.taskPriorities.map(type => type.id) || []

export const selectOldTaskStatusesAll = (
	state: RootState
): Array<SystemConfigValueParam<string, TaskStatusUnion>> =>
	state.systemConfig?.system?.taskStatuses || []

export const selectInternalTaskStatusesAll = (
	state: RootState
): SystemTaskStatusesV2['internalClinic'] =>
	state.systemConfig?.system?.taskStatusesV2?.internalClinic || []

export const selectExternalTaskStatusesAll = (
	state: RootState
): SystemTaskStatusesV2['externalClinic'] =>
	state.systemConfig?.system?.taskStatusesV2?.externalClinic || []

export const selectTaskStatusesAll = createSelector(
	[
		selectOldTaskStatusesAll,
		selectInternalTaskStatusesAll,
		selectExternalTaskStatusesAll,
	],
	(
		oldStatuses,
		internalClinicStatuses,
		externalClinicStatuses
	): Array<TaskStatusUnion | string> => {
		const oldTaskStatuses = oldStatuses.map(status => status.id)
		const newStatuses = [
			...internalClinicStatuses,
			...externalClinicStatuses,
		].map(status => status.value)

		return [...oldTaskStatuses, ...newStatuses]
	}
)

export const selectTaskAccountAll = createSelector(
	[selectTasks, selectOrganizationList],
	(tasks, allOrganizations) => {
		const organizations = tasks.map(task => {
			const organization =
				allOrganizations.find(
					o => o._id === task.taskOwner?.extra?.organizationID
				)?.orgName || ''
			return organization
		})
		return organizations.filter((item, index) => {
			return organizations.indexOf(item) === index
		})
	}
)

export const selectTasksList = createSelector(
	[
		selectTasks,
		selectOrganizationList,
		selectTaskSearch,
		selectedCheckedTaskTypes,
		selectedCeckedTaskPriorities,
		selectedCeckedTaskStatuses,
		selectedCeckedTaskAccountes,
		selectPatientsDataArray,
	],
	(
		tasks,
		allOrganizations,
		searchStr,
		checkedTaskTypes,
		ceckedTaskPriorities,
		ceckedTaskStatuses,
		ceckedTaskAccountes,
		patientsDataArray
	) => {
		const getOrgName = (org: any): string => {
			return allOrganizations.find(o => o._id === org)?.orgName || ''
		}
		return (
			(tasks
				.filter(task => {
					if (!searchStr) return true
					const patient = task.patient ? task.patient[0] : ''
					const org =
						getOrgName(task.taskOwner?.extra?.organizationID) || ''
					return (
						task.details?.title.includes(searchStr) ||
						task.taskOwner?.personal?.name.includes(searchStr) ||
						patient.includes(searchStr) ||
						org.includes(searchStr)
					)
				})
				.map(task => {
					if (
						checkedTaskTypes.length &&
						checkedTaskTypes.every(
							checkType =>
								!task?.details?.type?.includes(checkType)
						)
					) {
						return null
					}

					if (
						ceckedTaskPriorities.length &&
						ceckedTaskPriorities.every(
							checkPriority =>
								!task.settings?.priority.includes(checkPriority)
						)
					) {
						return null
					}

					if (
						ceckedTaskStatuses.length &&
						ceckedTaskStatuses.every(
							checkStatus =>
								!task?.settings?.status.includes(checkStatus)
						)
					) {
						return null
					}

					const account = getOrgName(
						task.taskOwner?.extra?.organizationID
					)
					if (
						ceckedTaskAccountes.length &&
						ceckedTaskAccountes.every(
							checkAccount => !account.includes(checkAccount)
						)
					) {
						return null
					}

					const getPatientName = (): string => {
						const patientName = patientsDataArray.find(
							(patient: PatientData) =>
								task.details?.patients[0] === patient._id
						)
						if (patientName)
							return (
								(patientName.firstName || '') +
								' ' +
								(patientName.lastName || '')
							)
						return ''
					}

					return {
						id: task._id,
						title: task?.details?.title,
						owner: task.taskOwner?.personal?.name || '',
						type: getAnyTitle(task?.details?.type || ''),
						due: task.settings?.dueDate,
						priority: task?.settings?.priority,
						patient: getPatientName() || '',
						account: account,
						status: task?.settings?.status,
						edit: task._id,
					}
				})
				.filter(c => !!c) as TasksTableCells[]) || []
		)
	}
)
